import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dumbbell, MessageSquare, LineChart, Video, ShieldCheck, Zap, Check, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Logo, Pill } from "../components/common";
import { useAuth } from "../context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1611672585731-fa10603fb9e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwbGlmdGluZyUyMHdlaWdodHMlMjBkYXJrJTIwZ3ltfGVufDB8fHx8MTc4MjUyNjQ1OHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Dumbbell, title: "Guided Workouts", desc: "Know exactly what to train each day with a phone-first workout player and rest timers." },
  { icon: Video, title: "Exercise Library", desc: "Demonstration videos, coaching cues and safe modifications for every movement." },
  { icon: MessageSquare, title: "Coach Messaging", desc: "Text, voice and video messages keep your coach in your corner all week long." },
  { icon: LineChart, title: "Progress Tracking", desc: "Weight, measurements, PRs and consistency tracked with clean interactive charts." },
  { icon: Zap, title: "Streaks & Accountability", desc: "Encouraging accountability scores that celebrate consistency, not shame." },
  { icon: ShieldCheck, title: "Private & Secure", desc: "Your health data, photos and videos stay private between you and your coach." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  const goDash = () => {
    if (!user) return navigate("/signup");
    navigate(user.role === "customer" ? "/app" : "/coach");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-5 h-28 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <button data-testid="nav-login-btn" onClick={() => navigate("/login")}
              className="px-4 py-2 text-sm font-semibold text-secondary hover:text-white transition-colors">Log in</button>
            <button data-testid="nav-signup-btn" onClick={() => navigate("/signup")}
              className="px-5 py-2 text-sm font-bold uppercase tracking-wide bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors">Get Started</button>
          </div>
        </div>
      </header>

      <section className="relative pt-28 min-h-[88vh] flex items-center overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-5 py-20 w-full">
          <div className="max-w-2xl fade-up">
            <Pill tone="danger">Personal Coaching Platform</Pill>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl mt-5 leading-[0.9]">
              Train Hard.<br />Climb <span className="text-brand">Higher.</span>
            </h1>
            <p className="text-lg text-secondary mt-6 max-w-lg leading-relaxed">
              Iron Mountain pairs you with a real coach who programs your training, reviews your form, and keeps you accountable — right from your phone.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button data-testid="hero-start-btn" onClick={goDash}
                className="group px-8 py-4 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover transition-all glow-red flex items-center gap-2">
                Start Training <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button data-testid="hero-pricing-btn" onClick={() => document.getElementById("pricing").scrollIntoView({ behavior: "smooth" })}
                className="px-8 py-4 border border-white/20 text-white font-bold uppercase tracking-wide rounded-lg hover:border-white transition-all">
                View Plans
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-24">
        <span className="label-xs">Everything you need</span>
        <h2 className="font-heading text-4xl sm:text-5xl mt-2">A coach in your pocket</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {FEATURES.map((f, i) => (
            <div key={i} className="card-surface rounded-xl p-6 hover:-translate-y-1 hover:border-brand transition-all duration-300">
              <div className="bg-brand/15 w-12 h-12 rounded-lg flex items-center justify-center">
                <f.icon className="w-6 h-6 text-brand" />
              </div>
              <h3 className="text-xl mt-4">{f.title}</h3>
              <p className="text-secondary text-sm mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center">
          <span className="label-xs">Membership</span>
          <h2 className="font-heading text-4xl sm:text-5xl mt-2">Choose your plan</h2>
          <p className="text-secondary mt-3">Cancel anytime. No equipment required to start.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {packages.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`rounded-2xl p-7 transition-all duration-300 ${p.featured ? "bg-brand/10 border-2 border-brand glow-red" : "card-surface hover:border-brand"}`}>
              {p.featured && <Pill tone="danger">Most Popular</Pill>}
              <h3 className="text-2xl mt-3">{p.name}</h3>
              <p className="text-secondary text-sm mt-2 min-h-[40px]">{p.description}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-heading text-5xl">${p.monthly_price}</span>
                <span className="text-secondary mb-1">/mo</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm">
                <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> {p.included_workouts} workouts / month</li>
                <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> {p.checkins} coach check-ins</li>
                {p.messaging && <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> Unlimited messaging</li>}
                {p.video_review && <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> Form video reviews</li>}
                {p.nutrition && <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> Nutrition coaching</li>}
                {p.trial_days > 0 && <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-success" /> {p.trial_days}-day free trial</li>}
              </ul>
              <button data-testid={`pricing-select-${p.id}`} onClick={() => navigate(`/signup?package=${p.id}`)}
                className={`w-full mt-7 py-3 font-bold uppercase tracking-wide rounded-lg transition-all ${p.featured ? "bg-brand text-white hover:bg-brand-hover" : "border border-white/20 hover:border-white"}`}>
                Choose {p.name}
              </button>
            </div>
          ))}
        </div>
        <p className="text-tertiary text-xs text-center mt-10 max-w-xl mx-auto">
          Iron Mountain does not replace professional medical advice. Consult a qualified medical professional before beginning any fitness program.
        </p>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <p className="text-tertiary text-sm">© 2026 Iron Mountain Fitness & Health</p>
        </div>
      </footer>
    </div>
  );
}
